import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { cursosAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AppSidebar } from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { BookOpen, Users, Play, Plus, X, Award, Clock, Star, TrendingUp } from 'lucide-react';

const API_BASE_URL = 'http://localhost:5000';

function Home() {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingMatricula, setLoadingMatricula] = useState({});
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    carregarCursos();
  }, []);

  useEffect(() => {
    if (!loading && cursos.length > 0) {
      const ctx = gsap.context(() => {
        // Animar cards
        const cards = containerRef.current?.querySelectorAll('.course-card');
        if (cards && cards.length > 0) {
          gsap.fromTo(
            cards,
            { opacity: 0, y: 30, scale: 0.9 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.6,
              stagger: 0.1,
              ease: "power2.out",
            }
          );
        }

        // Animar header
        const header = containerRef.current?.querySelector('.header-content');
        if (header) {
          gsap.fromTo(
            header,
            { opacity: 0, y: -20 },
            { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
          );
        }
      }, containerRef);

      return () => ctx.revert();
    }
  }, [loading, cursos]);

  const carregarCursos = async () => {
    try {
      setLoading(true);
      const response = await cursosAPI.listar();
      setCursos(response.data);
    } catch (err) {
      setError('Erro ao carregar cursos');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMatricula = async (cursoId, isMatriculado) => {
    try {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: true }));
      
      if (isMatriculado) {
        await cursosAPI.desmatricular(cursoId);
      } else {
        await cursosAPI.matricular(cursoId);
      }
      
      await carregarCursos();
    } catch (err) {
      console.error('Erro na matrícula:', err);
      alert(err.response?.data?.erro || 'Erro ao realizar operação');
    } finally {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: false }));
    }
  };

  const handleVerDetalhes = (cursoId) => {
    navigate(`/curso/${cursoId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 border-4 border-slate-600 border-t-red-600 rounded-full animate-spin mx-auto"></div>
              <h3 className="text-xl font-semibold text-white">Carregando cursos...</h3>
              <p className="text-slate-400">Preparando o melhor conteúdo para você</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen">
          <div className="flex items-center justify-center min-h-screen">
            <div className="bg-red-900/20 border border-red-600/30 text-red-400 p-6 rounded-xl text-center max-w-md">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Erro</h3>
              <p>{error}</p>
              <Button 
                onClick={carregarCursos} 
                className="mt-4 bg-red-600 hover:bg-red-700"
              >
                Tentar Novamente
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppSidebar />
      <div ref={containerRef} className="ml-64 min-h-screen">
        {/* Header moderno */}
        <div className="header-content border-b border-slate-800/50 bg-slate-950/50 backdrop-blur-xl sticky top-0 z-40">
          <div className="container mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
                  <BookOpen className="inline-block w-10 h-10 mr-3 text-red-500" />
                  Cursos Disponíveis
                </h1>
                <p className="text-slate-400 text-lg">
                  Descubra conhecimentos que vão transformar sua carreira
                </p>
              </div>
              
              {usuario?.nivel === 'admin' && (
                <Button 
                  onClick={() => navigate('/criar-curso')}
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Novo Curso
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Conteúdo principal */}
        <div className="container mx-auto px-6 py-8">
          {cursos.length === 0 ? (
            <div className="text-center py-24 space-y-6">
              <div className="w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-8">
                <BookOpen className="w-12 h-12 text-slate-400" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Nenhum curso encontrado</h3>
              <p className="text-xl text-slate-400 max-w-md mx-auto leading-relaxed">
                Novos cursos incríveis estão sendo preparados para você. Volte em breve!
              </p>
              <div className="mt-8">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600/20 text-blue-400 rounded-full border border-blue-600/30">
                  <Clock className="w-4 h-4" />
                  Em breve novidades
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cursos.map((curso) => (
                <Card 
                  key={curso.id} 
                  className="course-card group bg-slate-900/50 border-slate-800/50 hover:border-slate-700/50 transition-all duration-300 hover:transform hover:scale-[1.02] hover:shadow-2xl overflow-hidden"
                >
                  {/* Imagem do curso */}
                  {curso.capa && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={`${API_BASE_URL}/uploads/${curso.capa}`} 
                        alt={curso.titulo}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                      
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-red-600/90 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <Play className="w-6 h-6 text-white ml-1" />
                        </div>
                      </div>
                    </div>
                  )}

                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <CardTitle className="text-xl font-bold text-white group-hover:text-red-400 transition-colors line-clamp-2">
                        {curso.titulo}
                      </CardTitle>
                    </div>
                    
                    <CardDescription className="text-slate-300 line-clamp-3">
                      {curso.descricao || 'Curso completo e atualizado para você dominar esta tecnologia.'}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2">
                      {curso.categoria_nome && (
                        <Badge className="bg-red-600/20 text-red-400 border-red-600/30">
                          {curso.categoria_nome}
                        </Badge>
                      )}
                      {curso.empresa_nome && (
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {curso.empresa_nome}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Estatísticas do curso */}
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{curso.total_alunos || 0} aluno{curso.total_alunos !== 1 ? 's' : ''}</span>
                      </div>
                      {curso.duracao && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{curso.duracao}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Progresso (se matriculado) */}
                    {curso.matriculado && curso.progresso && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Progresso</span>
                          <span className="text-slate-300 font-medium">
                            {Math.round(curso.progresso.porcentagem)}%
                          </span>
                        </div>
                        <Progress 
                          value={curso.progresso.porcentagem} 
                          className="h-2"
                        />
                        <p className="text-xs text-slate-400">
                          {curso.progresso.videos_concluidos} de {curso.progresso.total_videos} vídeos concluídos
                        </p>
                        
                        {/* Badge de conclusão */}
                        {Math.round(curso.progresso.porcentagem) === 100 && (
                          <div className="flex items-center gap-2 text-green-400 text-sm">
                            <Award className="w-4 h-4" />
                            <span className="font-medium">Curso Concluído!</span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>

                  <CardFooter className="pt-0 space-y-3">
                    {/* Botão Ver Detalhes */}
                    <Button
                      onClick={() => handleVerDetalhes(curso.id)}
                      variant="outline"
                      className="w-full border-slate-600 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Ver Detalhes
                    </Button>
                    
                    {/* Botão Matrícula */}
                    <Button
                      onClick={() => handleMatricula(curso.id, curso.matriculado)}
                      disabled={loadingMatricula[curso.id]}
                      className={`w-full transition-all duration-200 ${
                        curso.matriculado 
                          ? 'bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-600/50' 
                          : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                      }`}
                    >
                      {loadingMatricula[curso.id] ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Processando...</span>
                        </div>
                      ) : curso.matriculado ? (
                        <div className="flex items-center gap-2">
                          <X className="w-4 h-4" />
                          <span>Desmatricular</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          <span>Matricular-se</span>
                        </div>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home; 